import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getAuditItems,
  addProcessToAudit as apiAddProcessToAudit,
  auditProcess as apiAuditProcess,
  addFeedback,
  getFeedbacks,
  getNotifications,
  addNotification as apiAddNotification,
  markNotificationAsRead as apiMarkNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification as apiDeleteNotification,
  clearAllNotifications as apiClearAllNotifications
} from '../api/request';

// 默认的Mermaid数据
const defaultMermaidData = `graph TD
    A[请点击输入您想制备的器件]`;

// 创建Context
const AppContext = createContext();

// Context Provider组件
export const AppProvider = ({ children }) => {
  // 主页面状态
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [mermaidData, setMermaidData] = useState(defaultMermaidData);
  const [currentChartData, setCurrentChartData] = useState(defaultMermaidData);
  const [detectedProcesses, setDetectedProcesses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formCompleted, setFormCompleted] = useState(false);
  const [shouldResetView, setShouldResetView] = useState(false);
  
  // 聊天相关状态
  const [chatMessages, setChatMessages] = useState([]);
  const [conversationId, setConversationId] = useState('');
  
  // 工艺数据状态
  const [uploadedProcesses, setUploadedProcesses] = useState([]);

  // 反馈数据状态
  const [feedbackItems, setFeedbackItems] = useState([]);

  // 审核数据状态
  const [auditItems, setAuditItems] = useState({
    pending: [],
    approved: [],
    rejected: []
  });

  // 加载状态
  const [isLoadingAuditItems, setIsLoadingAuditItems] = useState(false);

  // 通知状态
  const [notifications, setNotifications] = useState([]);

  // 弹窗状态
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    showCancel: false
  });

  // 同步审核数据到通知系统
  const syncAuditDataToNotifications = async (auditData) => {
    try {
      // 为所有已批准的工艺创建通知
      auditData.approved.forEach(async (item) => {
        const processName = item.processName || item.process_name || '工艺';
        const approveTime = item.approveTime || item.approve_time;
        if (approveTime) {
          await addNotification('success', `${processName} 审核通过`, `该工艺已批准使用 (${new Date(approveTime).toLocaleDateString()})`);
        }
      });

      // 为所有被拒绝的工艺创建通知
      auditData.rejected.forEach(async (item) => {
        const processName = item.processName || item.process_name || '工艺';
        const reason = item.rejectReason || item.reject_reason || '未提供原因';
        const rejectTime = item.approveTime || item.approve_time;
        if (rejectTime) {
          await addNotification('warning', `${processName} 审核被拒绝`, `拒绝原因: ${reason} (${new Date(rejectTime).toLocaleDateString()})`);
        }
      });

      // 为所有待审核的工艺创建通知
      auditData.pending.forEach(async (item) => {
        const processName = item.processName || item.process_name || '工艺';
        const submitTime = item.submitTime || item.submit_time;
        const isQuestionnaireType = item.feedbackType || item.feedback_type;
        if (submitTime) {
          if (isQuestionnaireType) {
            await addNotification('info', `问卷反馈已提交`, `${processName} 等待审核 (${new Date(submitTime).toLocaleDateString()})`);
          } else {
            await addNotification('info', `新工艺已提交`, `${processName} 等待审核 (${new Date(submitTime).toLocaleDateString()})`);
          }
        }
      });
    } catch (error) {
      console.error('同步审核数据到通知失败:', error);
    }
  };

  // 从数据库加载审核项目
  const loadAuditItems = async (showNotification = false, syncToNotifications = false) => {
    setIsLoadingAuditItems(true);
    try {
      const data = await getAuditItems();

      // 检查是否有新的审核结果（仅在非首次加载时）
      if (showNotification && auditItems.pending.length > 0) {
        const currentPendingIds = new Set(auditItems.pending.map(item => item.id));
        const newApproved = data.filter(item =>
          item.status === 'approved' && currentPendingIds.has(item.id)
        );
        const newRejected = data.filter(item =>
          item.status === 'rejected' && currentPendingIds.has(item.id)
        );

        // 为新审核的项目添加通知
        newApproved.forEach(item => {
          const processName = item.processName || item.process_name || '工艺';
          addNotification('success', `${processName} 审核通过`, '该工艺已批准使用');
        });

        newRejected.forEach(item => {
          const processName = item.processName || item.process_name || '工艺';
          const reason = item.rejectReason || item.reject_reason || '未提供原因';
          addNotification('warning', `${processName} 审核被拒绝`, `拒绝原因: ${reason}`);
        });
      }

      // 根据状态分组数据
      const groupedData = {
        pending: data.filter(item => item.status === 'pending'),
        approved: data.filter(item => item.status === 'approved'),
        rejected: data.filter(item => item.status === 'rejected')
      };
      setAuditItems(groupedData);

      // 如果需要同步到通知系统
      if (syncToNotifications) {
        console.log('自动同步已禁用，避免重复通知');
      }
    } catch (error) {
      console.error('加载审核项目失败:', error);
      if (showNotification) {
        addNotification('error', '加载审核数据失败', '请刷新页面重试');
      }
      // 如果API失败，使用默认数据
      setAuditItems({
        pending: [
          {
            id: 1,
            processName: '硅片清洗工艺',
            submitTime: '2025-01-10 14:30',
            type: '清洗',
            status: 'pending',
            description: '用于硅片表面清洗的标准工艺流程'
          }
        ],
        approved: [],
        rejected: []
      });
    } finally {
      setIsLoadingAuditItems(false);
    }
  };

  // 从数据库加载反馈项目
  const loadFeedbackItems = async () => {
    try {
      const data = await getFeedbacks();
      setFeedbackItems(data);
    } catch (error) {
      console.error('加载反馈项目失败:', error);
    }
  };

  // 从数据库加载通知
  const loadNotifications = async () => {
    try {
      const data = await getNotifications();
      // 转换数据库字段到前端格式
      const formattedNotifications = data.map(notification => ({
        id: notification.id,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        timestamp: new Date(notification.created_at),
        isRead: notification.is_read
      }));
      setNotifications(formattedNotifications);
    } catch (error) {
      console.error('加载通知失败:', error);
    }
  };

  // 组件挂载时加载数据
  useEffect(() => {
    const initializeData = async () => {
      // 先加载通知
      await loadNotifications();
      // 再加载审核数据，如果通知为空则同步审核数据到通知
      const currentNotifications = notifications.length;
      await loadAuditItems(false, currentNotifications === 0);
      await loadFeedbackItems();
    };

    initializeData();
  }, []);

  // 定期检查审核状态更新（每30秒）
  useEffect(() => {
    const interval = setInterval(() => {
      loadAuditItems(true); // 启用通知检查
    }, 30000);

    return () => clearInterval(interval);
  }, [auditItems.pending]);

  // 重置主页面状态
  const resetHomeState = () => {
    setMermaidData(defaultMermaidData);
    setCurrentChartData(defaultMermaidData);
    setShouldResetView(true);
    setDetectedProcesses([]);
    setShowForm(false);
    setFormCompleted(false);
    setChatMessages([]);
    setConversationId('');
  };

  // 添加新的工艺到审核列表
  const addProcessToAudit = async (processData) => {
    try {
      const newProcess = {
        processName: processData.processName,
        type: processData.processType,
        description: processData.description
      };

      const savedProcess = await apiAddProcessToAudit(newProcess);

      // 更新本地状态
      setAuditItems(prev => ({
        ...prev,
        pending: [...prev.pending, savedProcess]
      }));

      setUploadedProcesses(prev => [...prev, savedProcess]);

      // 添加成功通知
      addNotification('success', '新工艺提交成功', '您的工艺已提交，等待审核');

      return savedProcess;
    } catch (error) {
      console.error('添加工艺到审核列表失败:', error);
      // 添加失败通知
      addNotification('error', '新工艺提交失败', '请稍后重试或联系管理员');
      throw error;
    }
  };

  // 审核工艺
  const auditProcess = async (id, action, reason = '') => {
    try {
      const updatedProcess = await apiAuditProcess(id, action, reason);

      // 更新本地状态
      setAuditItems(prev => {
        const processToMove = prev.pending.find(item => item.id === id);
        if (!processToMove) return prev;

        return {
          ...prev,
          pending: prev.pending.filter(item => item.id !== id),
          [action === 'approve' ? 'approved' : 'rejected']: [
            ...prev[action === 'approve' ? 'approved' : 'rejected'],
            updatedProcess
          ]
        };
      });

      // 添加审核结果通知
      const isApproved = action === 'approve';
      const processName = updatedProcess.processName || updatedProcess.process_name || '工艺';
      addNotification(
        isApproved ? 'success' : 'warning',
        `${processName} 审核${isApproved ? '通过' : '被拒绝'}`,
        isApproved ? '该工艺已批准使用' : `拒绝原因: ${reason || '未提供原因'}`
      );

      return updatedProcess;
    } catch (error) {
      console.error('审核工艺失败:', error);
      // 添加失败通知
      addNotification('error', '审核操作失败', '请稍后重试或联系管理员');
      throw error;
    }
  };

  // 通知管理函数
const addNotification = async (type, title, message = '') => {
  try {
    // ✅ 先检查本地是否已存在相同通知
    const existingNotification = notifications.find(n => 
      n.type === type && 
      n.title === title && 
      n.message === message
    );
    
    if (existingNotification) {
      console.log('本地已存在相同通知，跳过添加:', existingNotification);
      return existingNotification.id;
    }

    // 保存到数据库
    const savedNotification = await apiAddNotification(type, title, message);

    // ✅ 检查后端返回的是否是已存在的通知
    if (savedNotification.isExisting) {
      console.log('后端返回已存在通知，跳过本地添加');
      return savedNotification.id;
    }

    // 转换数据库字段到前端格式并更新本地状态
    const formattedNotification = {
      id: savedNotification.id,
      type: savedNotification.type,
      title: savedNotification.title,
      message: savedNotification.message,
      timestamp: new Date(savedNotification.created_at),
      isRead: savedNotification.is_read
    };

    setNotifications(prev => [formattedNotification, ...prev]);
    return savedNotification.id;
  } catch (error) {
    console.error('添加通知失败:', error);
    // 如果数据库操作失败，仍然添加到本地状态（降级处理）
    const notification = {
      id: Date.now() + Math.random(),
      type,
      title,
      message,
      timestamp: new Date(),
      isRead: false
    };
    setNotifications(prev => [notification, ...prev]);
    return notification.id;
  }
};

  const removeNotification = async (id) => {
    // 乐观更新：先立即更新UI
    setNotifications(prev => prev.filter(n => n.id !== id));

    // 后台同步数据库
    try {
      await apiDeleteNotification(id);
    } catch (error) {
      console.error('删除通知失败:', error);
      // 如果数据库操作失败，可以选择回滚或显示错误提示
      // 这里选择保持UI状态，因为用户已经看到删除效果
    }
  };

  const clearAllNotifications = async () => {
    // 乐观更新：先立即清空UI
    setNotifications([]);

    // 后台同步数据库
    try {
      await apiClearAllNotifications();
    } catch (error) {
      console.error('清空通知失败:', error);
      // 如果数据库操作失败，可以选择回滚或显示错误提示
      // 这里选择保持UI状态，因为用户已经看到清空效果
    }
  };

  const markNotificationAsRead = async (id) => {
    // 乐观更新：先立即更新UI
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id
          ? { ...notification, isRead: true }
          : notification
      )
    );

    // 后台同步数据库
    try {
      await apiMarkNotificationAsRead(id);
    } catch (error) {
      console.error('标记通知已读失败:', error);
      // 如果数据库操作失败，可以选择回滚或显示错误提示
      // 这里选择保持UI状态，因为用户已经看到标记效果
    }
  };

  const markAllNotificationsAsRead = async () => {
    // 乐观更新：先立即更新UI
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, isRead: true }))
    );

    // 后台同步数据库
    try {
      await markAllNotificationsAsRead();
    } catch (error) {
      console.error('批量标记通知已读失败:', error);
      // 如果数据库操作失败，可以选择回滚或显示错误提示
      // 这里选择保持UI状态，因为用户已经看到标记效果
    }
  };

  // 弹窗管理函数
  const showAlert = (type, title, message, options = {}) => {
    return new Promise((resolve) => {
      setAlertModal({
        isOpen: true,
        type,
        title,
        message,
        showCancel: options.showCancel || false,
        confirmText: options.confirmText || '确定',
        cancelText: options.cancelText || '取消',
        onConfirm: () => {
          setAlertModal(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setAlertModal(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        }
      });
    });
  };

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  // 添加反馈到审核列表
  const addFeedbackToAudit = async (feedbackData) => {
    try {
      const newFeedback = {
        processName: `问卷反馈 - ${feedbackData.processType}`,
        type: '问卷反馈',
        description: `问题: ${feedbackData.question}\n反馈内容: ${feedbackData.content}`,
        feedbackType: 'questionnaire',
        originalQuestion: feedbackData.question,
        feedbackContent: feedbackData.content,
        processType: feedbackData.processType,
        questionId: feedbackData.questionId
      };

      const savedFeedback = await addFeedback(newFeedback);

      // 更新本地状态
      setAuditItems(prev => ({
        ...prev,
        pending: [...prev.pending, savedFeedback]
      }));

      setFeedbackItems(prev => [...prev, savedFeedback]);

      // 添加成功通知
      addNotification('success', '反馈提交成功', '您的反馈已提交，等待审核');

      return savedFeedback;
    } catch (error) {
      console.error('添加反馈失败:', error);
      // 添加失败通知
      addNotification('error', '反馈提交失败', '请稍后重试或联系管理员');
      throw error;
    }
  };

  const contextValue = {
    // 主页面状态
    isChatVisible,
    setIsChatVisible,
    mermaidData,
    setMermaidData,
    currentChartData,
    setCurrentChartData,
    detectedProcesses,
    setDetectedProcesses,
    showForm,
    setShowForm,
    formCompleted,
    setFormCompleted,
    shouldResetView,
    setShouldResetView,

    // 聊天状态
    chatMessages,
    setChatMessages,
    conversationId,
    setConversationId,

    // 工艺数据
    uploadedProcesses,
    auditItems,
    feedbackItems,
    isLoadingAuditItems,

    // 通知系统
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    loadNotifications,
    syncAuditDataToNotifications,

    // 弹窗系统
    alertModal,
    showAlert,
    closeAlert,

    // 方法
    resetHomeState,
    addProcessToAudit,
    auditProcess,
    addFeedbackToAudit,
    loadAuditItems,
    loadFeedbackItems,

    // 常量
    defaultMermaidData
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// 自定义Hook来使用Context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
